import type { Article } from "@/mocks/articles";

interface ArticleContentProps {
  article: Article;
}

export default function ArticleContent({ article }: ArticleContentProps) {
  return (
    <div className="space-y-12">
      <section id="overview" className="scroll-mt-28">
        <h2 className="mb-4 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-600">
            <i className="ri-article-line text-base" />
          </span>
          事件概述
        </h2>
        <p className="mb-4 text-base leading-relaxed text-foreground-700">{article.subtitle}</p>
        <p className="text-base leading-relaxed text-foreground-700">{article.summary}</p>

        <div className="mt-6 rounded-lg border-l-4 border-secondary-400 bg-secondary-50 p-5">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-secondary-900">
            <i className="ri-file-text-line" />
            原始爆料帖
          </h3>
          <p className="mb-3 text-sm leading-relaxed text-foreground-700">
            “{article.originalPost.text}”
          </p>
          <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-voice-line" />
              {article.originalPost.handle}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-apps-line" />
              {article.originalPost.platform}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {article.originalPost.time}
            </span>
          </p>
        </div>
      </section>

      <section id="screens" className="scroll-mt-28">
        <h2 className="mb-4 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-image-2-line text-base" />
          </span>
          关键截图
        </h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {article.screenshots.map((shot) => (
            <figure
              key={shot.url}
              className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
            >
              <div className="h-48 w-full overflow-hidden bg-background-100 md:h-56">
                <img src={shot.url} alt={shot.alt} className="h-full w-full object-cover object-top" />
              </div>
              <figcaption className="px-4 py-3 text-xs leading-relaxed text-foreground-600">
                {shot.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {article.sections.map((section, index) => (
        <section
          key={section.heading}
          id={`section-${index + 1}`}
          className="scroll-mt-28"
        >
          <h2 className="mb-4 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-200 text-foreground-700">
              {String(index + 1).padStart(2, "0")}
            </span>
            {section.heading}
          </h2>
          <div className="space-y-4">
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph} className="text-base leading-relaxed text-foreground-700">
                {paragraph}
              </p>
            ))}
          </div>
        </section>
      ))}

      <section id="timeline" className="scroll-mt-28">
        <h2 className="mb-6 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-600">
            <i className="ri-time-line text-base" />
          </span>
          关键节点时间线
        </h2>
        <ol className="relative space-y-6 border-l border-background-300 pl-6">
          {article.timeline.map((item) => (
            <li key={`${item.time}-${item.title}`} className="relative">
              <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 border-background-50 bg-primary-500" />
              <span className="mb-1 block text-xs font-medium tracking-wider text-primary-600">
                {item.time}
              </span>
              <h3 className="mb-1 text-sm font-semibold text-foreground-950 md:text-base">
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      <section id="followup" className="scroll-mt-28">
        <h2 className="mb-4 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-refresh-line text-base" />
          </span>
          后续进展
        </h2>
        <ul className="space-y-3 rounded-lg border border-background-200 bg-background-100 p-5">
          {article.followUp.map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
              <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
        <span className="mr-1 text-sm text-foreground-500">相关标签：</span>
        {article.tags.map((tag) => (
          <span
            key={tag}
            className="whitespace-nowrap rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs text-foreground-700"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}